import { debounce, flatMap, isEqual } from "lodash";
import * as React from "react";
import { findDOMNode } from "react-dom";
import styled from "styled-components";

import { DocItem, DocItemGroup } from "../../../data/tos";
import { InOffAnimationProps, onOffAnimation } from "../../styles/animations";
import { colors, viewports } from "../../styles/variables";
import { rAFAnimate } from "../../utils/rAFAnimate";
import Menu, { MENU_HEIGHT } from "../Menu";
import { THUMBNAIL_HEIGHT, THUMBNAIL_TRANSITION_DURATION } from "../SectionThumbnail";
import ItemGroupIcon from "../shared/ItemGroupIcon";

import DocumentItem from "./DocumentItem";

interface ItemState {
  read: boolean;
  topPosition: number;
  bottomPosition: number;
}

const defaultItemState = {
  read: false,
  topPosition: 0,
  bottomPosition: 0
};

interface Props {
  title: string;
  description: string;
  averageReadTime: number;
  itemGroups: DocItemGroup[];
  background: string;

  visible: boolean;
  close: () => void;

  validated: boolean;
  /** Callback to lift state up when this section has been validated */
  onSectionValidate: () => void;
}

interface State {
  itemsState: ItemState[];
  submitButtonVisible: boolean;
}

// TODO: memoization?
const flattenItems = (itemGroups: DocItemGroup[]) => flatMap(itemGroups.map(iG => iG.items));
const flattenItemsTitles = (items: DocItem[]) => items.map(i => i.title);

// WARNING: use parent scrollTop to calculate read status
export default class DocumentSection extends React.Component<Props, State> {
  state: State = {
    itemsState: flattenItems(this.props.itemGroups).map(_ => ({ ...defaultItemState })),
    submitButtonVisible: false
  };

  private lastScrollY = 0;

  /** TMP: store the parent node (necessary to respond to scroll events) */
  private node: any;

  // TODO: reset when itemGroups props changes
  /** Cached value for perf purpose: avoid trigger unnecessary menu animations */
  private currentMenuIndex = 0;

  private menuRef = React.createRef<Menu>();

  private handleScroll = debounce(() => {
    const newBottomScrollPosition = this.node.scrollTop + this.node.getBoundingClientRect().height;
    const newState: Partial<State> = {};

    // ------------- 1. Submit button appears -----------
    if (!this.state.submitButtonVisible && newBottomScrollPosition > 200) {
      newState.submitButtonVisible = true;
    }

    // ------------- 2. Automatically update curent menu item -----------
    this.updateCurrentMenuItem(newBottomScrollPosition);

    // ------------- 3. Mark items above current scroll position  as read ------
    if (this.lastScrollY <= newBottomScrollPosition) {
      const newItemsState = [...this.state.itemsState];
      let changed = false;
      this.state.itemsState.map((iS, index) => {
        if (!iS.read && iS.bottomPosition < newBottomScrollPosition) {
          changed = true;
          newItemsState[index] = { ...this.state.itemsState[index], read: true };
        }
      });
      if (changed) {
        newState.itemsState = newItemsState;
      }
    }

    this.lastScrollY = newBottomScrollPosition;
    if (!isEqual(newState, {})) {
      window.requestAnimationFrame(() => {
        this.setState(newState);
      });
    }
  }, 150);

  updateCurrentMenuItem = (newBottomScrollPosition: number) => {
    let maxPos = 0;
    let maxIndex = 0;
    const middleOfScreen = newBottomScrollPosition - this.node.getBoundingClientRect().height / 2;
    this.state.itemsState.map((iS, index) => {
      if (iS.topPosition < middleOfScreen && iS.topPosition > maxPos) {
        maxPos = iS.topPosition;
        maxIndex = index;
      }
    });
    if (maxIndex !== this.currentMenuIndex && this.menuRef.current) {
      this.currentMenuIndex = maxIndex;
      this.menuRef.current.selectItem(maxIndex);
    }
  };

  componentDidMount() {
    this.node = findDOMNode(this)!.parentNode;
    this.node.addEventListener("scroll", this.handleScroll);
  }

  componentWillUnmount() {
    this.node.removeEventListener("scroll", this.handleScroll.cancel);
  }

  handleItemPositionUpdate(index: number) {
    return (topPosition: number, bottomPosition: number) => {
      this.setState(({ itemsState }) => ({
        itemsState: [...itemsState.slice(0, index), { ...itemsState[index], topPosition, bottomPosition }, ...itemsState.slice(index + 1)]
      }));
    };
  }

  handleValidation = () => {
    this.props.onSectionValidate();
    this.props.close();
  };

  handleMenuClick = (index: number) => {
    const initialScrollTop = this.node.scrollTop;
    const scrollTopAnimation = (percentage: number) => {
      this.node.scrollTop = Math.floor(
        initialScrollTop + (this.state.itemsState[index].topPosition - MENU_HEIGHT - 10 - initialScrollTop) * percentage
      );
    };
    rAFAnimate(scrollTopAnimation);
  };

  render() {
    const readItems = this.state.itemsState.reduce((acc: number, val) => {
      return val.read ? acc + 1 : acc;
    }, 0);
    const remainingItems = this.state.itemsState.length - readItems;

    /** Quick imperative hack: we can't get an item index when using map on the non-flattened itemGroups, we use this shared variable to keep track of the current index  */
    let itemIndex = -1;

    return (
      <StyledSection zIndex={this.props.visible ? 100 : 1}>
        <IllustrationPicture visible={this.props.visible} onClick={this.props.close} background={this.props.background}>
          <HeaderContainer>
            <ReadTime visible={this.props.visible}>{this.props.averageReadTime / 60} minutes</ReadTime>
            <ThumbnailTitle visible={this.props.visible}>{this.props.title}</ThumbnailTitle>
          </HeaderContainer>
        </IllustrationPicture>

        <FlexContainer>
          <StickyMenu visible={this.props.visible}>
            <Menu items={flattenItemsTitles(flattenItems(this.props.itemGroups))} ref={this.menuRef} onMenuClick={this.handleMenuClick} />
          </StickyMenu>

          <StyledFakePageContent visible={this.props.visible}>
            <div>
              <MiddleLine />
              {this.props.itemGroups.map((iG, index) => (
                <ItemGroups key={index}>
                  <ItemGroupIcon icon={iG.icon} />
                  {iG.items.map((item, iindex) => {
                    itemIndex++;
                    return (
                      <DocumentItem
                        paragraphs={item.paragraphs}
                        title={item.title}
                        read={this.state.itemsState[itemIndex].read}
                        onPositionUpdate={this.handleItemPositionUpdate(itemIndex)}
                        key={iindex}
                      />
                    );
                  })}
                </ItemGroups>
              ))}
            </div>
          </StyledFakePageContent>
        </FlexContainer>

        <ValidateButtonContainer>
          <ValidateButton
            active={remainingItems === 0}
            visible={this.props.visible && this.state.submitButtonVisible}
            onClick={remainingItems === 0 ? this.handleValidation : undefined}
          >
            {remainingItems > 0 ? `Plus que ${remainingItems} item${remainingItems > 1 ? "s" : ""} à lire` : "J'ai lu et j'accepte"}
          </ValidateButton>
        </ValidateButtonContainer>
      </StyledSection>
    );
  }
}

interface StyledSectionProps {
  zIndex: number;
}

const StyledSection = styled.div<StyledSectionProps>`
  position: relative;
  z-index: ${({ zIndex }) => zIndex};
`;

const StickyMenu = styled.div<InOffAnimationProps>`
  position: sticky;
  top: 0;
  z-index: 400;
  ${onOffAnimation({ duration: 200, delay: THUMBNAIL_TRANSITION_DURATION + 50, easing: "ease-out" }, { y: -5, delay: 0 })};
`;

const FlexContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StyledFakePageContent = styled.div<InOffAnimationProps>`
  position: relative;
  background: white;
  padding: 20px;
  ${onOffAnimation({ duration: 350, delay: THUMBNAIL_TRANSITION_DURATION + 100 }, { delay: 0 })};
`;

interface IllustrationPictureProps {
  background: string;
}

const IllustrationPicture = styled.div<IllustrationPictureProps & InOffAnimationProps>`
  position: relative;
  width: 100%;
  height: ${THUMBNAIL_HEIGHT}px;
  background-position: center;
  background-size: cover;
  background-image: linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)), url(${props => props.background});
  color: white;
  cursor: pointer;
  ${onOffAnimation({ duration: 1000, delay: THUMBNAIL_TRANSITION_DURATION + 20 }, { duration: 100, delay: 0, y: 0 })};

  ${viewports.desktop`
    max-width: 800px;
    margin: 0 auto;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    border-radius: 0 0 2px 2px;
  `};
`;

// Combining position-sticky + margin-bottom + backgroudn-color does not work.
// We use two different DIVs: ValidateButtonContainer and ValidateButton
const ValidateButtonContainer = styled.div`
  position: sticky;
  bottom: 0;
  ${viewports.desktop`
    padding: 20px;
  `};
`;

interface ValidateButtonProps {
  active: boolean;
}

const ValidateButton = styled.div<ValidateButtonProps & InOffAnimationProps>`
  text-align: center;
  color: white;
  padding: 1rem 10px;
  font-weight: 600;
  background: ${({ active }) => (active ? colors.brand : "#bdbdbd")};
  cursor: pointer;
  ${onOffAnimation({ duration: 200 }, {})};
  ${viewports.desktop`
    max-width: 600px;
    margin: 0 auto;
    border-radius: 3px;
  `};
`;

const ItemGroups = styled.div`
  position: relative;
`;

const MiddleLine = styled.div`
  position: absolute;
  left: 50%;
  width: 2px;
  height: calc(100% - 100px);
  margin-top: 20px;
  background: ${colors.brand};
  opacity: 0.2;
  ${viewports.desktop`
    height: calc(100% - 80px)
  `};
`;

const HeaderContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-end;
  height: calc(100% - 20px);
  ${viewports.desktop`
    justify-content: center;
    text-align: center;
  `};
`;

const ThumbnailTitle = styled.div<InOffAnimationProps>`
  font-size: 1.6rem;
  line-height: 120%;
  font-weight: 700;
  padding: 0 20px;
  ${onOffAnimation({ delay: THUMBNAIL_TRANSITION_DURATION }, { y: 20 })};
`;

const ReadTime = styled.div<InOffAnimationProps>`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 20px;
  margin-left: 20px;
  margin-right: 20px;
  text-transform: uppercase;
  letter-spacing: 1px;
  ${onOffAnimation({ delay: THUMBNAIL_TRANSITION_DURATION + 50 }, { y: 20 })};
`;



// WEBPACK FOOTER //
// ./src/components/structure/DocumentSection.tsx